import React, { useCallback, useMemo } from 'react';
import { QuestionResultToView } from '@sapphicsavvy/business';
import { useSignedInContext } from '../../../../context/SignedInContext';
import { mbGetMediumImage } from '@mightybyte/rnw.components.image';
import Layout from '../components/Layout';
import {
    FlatList,
    Image,
    Platform,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { FONT_STYLES, textStyles } from '../../../../constants/textStyles';
import { Spacer } from '../../../helperComponents/Spacer';
import { imageUtils } from '../../../../utils/imageUtils';
import InfoCard from '../components/InfoCard';
import { getNumberOfSteps } from '../../../../utils/shared';
import { AnimatedFastImage, AnimatedView, seconds } from '../../../../utils/animations';

type OtherGroupBonusStepProps = {
    onNext?: () => void;
    onBack?: () => void;
    points: number;
    results: QuestionResultToView;
};

export default function OtherGroupBonusStep({
    points,
    onBack,
    onNext,
    results,
}: OtherGroupBonusStepProps) {
    const { currentUserData } = useSignedInContext();
    const imageUri = mbGetMediumImage(results.imageUrl, { asUri: true });

    const groups: QuestionResultToView['otherGroupBonusResults'] =
        useMemo(() => {
            return results.otherGroupBonusResults ?? [];
        }, [results.otherGroupBonusResults]);

    const bonusSum = useMemo(() => {
        return groups.reduce((sum, group) => {
            return sum + group.bonusPoints;
        }, 0);
    }, [groups]);

    const itemSeparator = useCallback(() => <Spacer height={15} />, []);

    return (
        <Layout
            title="OTHER GROUP BONUS"
            titleStyle={styles.title}
            points={points}
            numberOfSteps={getNumberOfSteps(results)}
            currentStep={4}
            onBack={onBack}
            onNext={onNext}
            nextButtonColor="#FCAF41F4"
            topSectionStyle={styles.topSection}
            titleColor="#FCAF41F4"
            topSectionChildren={
                <View style={styles.topSectionInfo}>
                    <Text style={styles.topSectionTitleText}>
                        {results.identityGroupBonus?.question}
                    </Text>
                    <AnimatedFastImage
                        fromScale={0.8}
                        toScale={1}
                        resizeMode={Platform.OS === 'ios' ? 'contain' : 'cover'}
                        style={styles.topSectionImage}
                        source={imageUri}
                    />
                </View>
            }
            bottomSectionStyle={styles.bottomSection}
            overlay
        >
            <FlatList
                style={styles.list}
                data={groups}
                contentContainerStyle={styles.listContent}
                renderItem={({ item }) => (
                    <InfoCard
                        type={results.answerType}
                        title={item.groupType}
                        userValue={item.userGuess}
                        correctValue={item.surveyResult}
                        userImage={
                            <View style={styles.userImageContainer}>
                                <Image
                                    style={styles.userImage}
                                    source={mbGetMediumImage(
                                        currentUserData?.avatarUrl,
                                        { asUri: true },
                                    )}
                                />
                            </View>
                        }
                        correctImage={
                            <View style={styles.logoContainer}>
                                <Image
                                    style={styles.logo}
                                    source={imageUtils.screens.logoHomeScreen}
                                    resizeMode="contain"
                                />
                            </View>
                        }
                        userColor="#EE3823"
                        correctColor="#6A2C8C"
                        resultType={item.resultType}
                        min={results.answerOptions.min}
                        max={results.answerOptions.max}
                        animated
                    />
                )}
                ItemSeparatorComponent={itemSeparator}
            />
            {bonusSum <= 0 ? (
                <View style={styles.note}>
                    <AnimatedView
                        fromHeight={0}
                        toHeight={58}
                        style={styles.wrongAnswer}
                        delay={seconds(1)}
                    >
                        <Text style={styles.answerText}>WRONG! 0 POINTS</Text>
                    </AnimatedView>
                </View>
            ) : (
                <View style={styles.note}>
                    <AnimatedView
                        fromHeight={0}
                        toHeight={58}
                        style={styles.correctAnswer}
                        delay={seconds(1)}
                    >
                        <Text style={styles.answerText}>
                            YOU WON {bonusSum} POINTS
                        </Text>
                    </AnimatedView>
                </View>
            )}
        </Layout>
    );
}

const styles = StyleSheet.create({
    title: {
        fontSize: 22,
    },
    topSection: {
        backgroundColor: '#FCAF41F4',
        height: '35%',
    },
    topSectionInfo: {
        paddingVertical: 20,
        paddingHorizontal: 20,
        flex: 1,
    },
    topSectionTitleText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 21,
            fontWeight: '600',
            marginBottom: 20,
        },
    ]),
    topSectionImage: Platform.select({
        default: {
            flex: 1,
            alignSelf: 'stretch',
            borderRadius: 12,
            marginHorizontal: 10,
        },
        android: {
            flex: 1,
            aspectRatio: 2,
            borderRadius: 12,
            marginHorizontal: 10,
            alignSelf: 'center',
        },
    }),
    bottomSection: {
        justifyContent: 'flex-start',
    },
    list: {
        flex: 1,
        alignSelf: 'stretch',
    },
    listContent: {
        padding: 15,
        paddingBottom: 80,
    },
    userImageContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderRadius: 360,
        borderColor: '#D9D9D9',
    },
    userImage: {
        width: 31,
        height: 31,
        borderRadius: 360,
    },
    logoContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderRadius: 360,
        borderColor: 'transparent',
    },
    logo: {
        width: 31,
        height: 31,
    },
    note: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 10,
        height: 58,
    },
    wrongAnswer: {
        backgroundColor: '#EE3823',
        alignItems: 'center',
        justifyContent: 'center',
    },
    answerText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 22,
            fontFamily: FONT_STYLES.Tabasco_400,
            fontWeight: '400',
        },
    ]),
    correctAnswer: {
        backgroundColor: '#0295CD',
        alignItems: 'center',
        justifyContent: 'center',
    },
});
